// Wire contract for the SH-1408 backend endpoint `GET /Calendar/Events`.
//
// This is the SERVER shape. It is deliberately separate from the presentational
// `CalendarEvent` (typings.ts); `mapCalendarEvent()` adapts one to the other, so
// a backend field rename never ripples into the components. Sourced from the
// SH-1408 Engineer Implementation Spec v1.3, reconciled against the merged
// backend (CalendarEventDTO.cs / CalendarEventListResponse).

export type CalendarWireSource = 'SC' | 'PestPac' | 'Google' | 'Outlook'
export type CalendarWireVisibility = 'full' | 'busy'
export type CalendarWireEntityType = 'Lead' | 'Opportunity' | 'PendingLead'

// All-day convention (the backend stores whatever it is sent, so every writer
// must follow this): `startTime` is UTC midnight of the first day, `endTime` is
// UTC midnight of the day AFTER the last day (exclusive). A one-day event on
// 25 Sep is 2026-09-25T00:00:00Z → 2026-09-26T00:00:00Z. The reader tolerates a
// same-day or zero-length end (treated as one day) and an end with a time of
// day (that date counts as included).
export type CalendarEventDTO = {
  id: number
  title: string
  description?: string | null
  /** ISO 8601 UTC. */
  startTime: string
  /** ISO 8601 UTC; exclusive for all-day events (see convention above). */
  endTime: string
  isAllDay: boolean
  source: CalendarWireSource
  sourceId: string
  isReadOnly: boolean
  /** Null on `busy` (non-owner) events — the server strips it. */
  eventType: string | null
  color?: string | null
  entityType?: CalendarWireEntityType | null
  entityId?: number | null
  /** Numeric on the wire (`Activity.ActivityTypeId`); only set for EventType "Activity". Converted to string in mapCalendarEvent. */
  activityTypeId?: number | null
  deepLink?: string | null
  visibility: CalendarWireVisibility
  /**
   * Activity-only fields the SC-events feed (SH-1419) adds for the detail
   * popover's Type line and editable Status chip. Optional: the SH-1408
   * standalone-event feed does not send them, so the popover hides those rows
   * until they are present (SH-1986, Q3).
   */
  status?: string | null
  activityType?: string | null
  /**
   * The id of the ACTIVITY behind an activity-reminder event — the record the
   * status write addresses. Deliberately NOT derived from `id` (a calendar-event
   * id) or `sourceId` (a string of unspecified provenance): the status chip is
   * gated on this field, so if the SC-events feed names it differently the chip
   * stays hidden rather than writing to whatever `id` happens to be.
   */
  activityId?: number | null
}

// The server sends a plain message string today; the object form is kept so a
// richer per-source error payload later is not a breaking change here.
export type CalendarSourceError =
  | string
  | ({ message?: string } & Record<string, unknown>)

export type CalendarEventsResponse = {
  events: CalendarEventDTO[]
  paging: { skip: number; take: number; total: number }
  /** Non-blocking partial-failure map, keyed by source (SH-1408 REQ-7). */
  errors: Record<string, CalendarSourceError>
}

export type FetchCalendarParams = {
  start: Date
  end: Date
  skip?: number
  take?: number
  signal?: AbortSignal
}

/** Mirrors CalendarQueryValidator: MaxRangeDays = 31, MaxTake = 1000. */
export const MAX_QUERY_DAYS = 31
export const MAX_TAKE = 1000
